import { fetchApi, handleJson } from '@/api/http'
import type { CSSProperties } from 'react'
import { isPlatformPath } from '@/config/platform'
import { getTenantSlug, hasTenantBound } from '@/config/tenant'
import {
  DEFAULT_CATALOG_KEY,
  CUSTOM_STYLE_ID,
  findCatalogStyleByKey,
  parseCatalogKey,
  type ThemePaletteColors,
} from '@/data/whiteLabelThemeCatalog'
import { getAdminTenantSlug } from '@/utils/adminAuth'
import { buildThemeFromPalette, refineStoredTheme } from '@/utils/themePalette'

const THEME_CACHE_PREFIX = 'agendamentos-app:theme:'
const MARKETING_LANDING_THEME_COLOR = '#0f172a'

export interface AppThemeConfig {
  bg: string
  bgCard: string
  bgElevated: string
  text: string
  textMuted: string
  accent: string
  accentHover: string
  accentForeground: string
  border: string
  borderSubtle: string
  radius: string
  radiusSm: string
  /** Referência ao catálogo white-label (ex.: tecnologia:clean-sw ou barbershop:custom). */
  catalogKey?: string
  /** Cores 60/30/10 quando catalogKey termina em :custom. */
  customPalette?: ThemePaletteColors
}

const defaultPalette = findCatalogStyleByKey(DEFAULT_CATALOG_KEY)
export const DEFAULT_THEME: AppThemeConfig = defaultPalette
  ? buildThemeFromPalette(defaultPalette.style, DEFAULT_CATALOG_KEY)
  : {
      bg: '#f8fafc',
      bgCard: '#ffffff',
      bgElevated: '#f1f5f9',
      text: '#1e293b',
      textMuted: '#64748b',
      accent: '#2563eb',
      accentHover: '#1d4ed8',
      accentForeground: '#ffffff',
      border: 'rgba(37, 99, 235, 0.14)',
      borderSubtle: 'rgba(37, 99, 235, 0.07)',
      radius: '16px',
      radiusSm: '10px',
      catalogKey: DEFAULT_CATALOG_KEY,
    }

type ThemeCssKey = Exclude<keyof AppThemeConfig, 'catalogKey' | 'customPalette'>

function isPaletteColors(value: unknown): value is ThemePaletteColors {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v.primaria === 'string' &&
    typeof v.secundaria === 'string' &&
    typeof v.destaque === 'string'
  )
}

const THEME_MAP: Record<ThemeCssKey, string> = {
  bg: '--bg',
  bgCard: '--bg-card',
  bgElevated: '--bg-elevated',
  text: '--text',
  textMuted: '--text-muted',
  accent: '--accent',
  accentHover: '--accent-hover',
  accentForeground: '--accent-foreground',
  border: '--border',
  borderSubtle: '--border-subtle',
  radius: '--radius',
  radiusSm: '--radius-sm',
}

function isThemeObject(value: unknown): value is Partial<AppThemeConfig> {
  return typeof value === 'object' && value !== null
}

export function parseTheme(raw: string | null | undefined): AppThemeConfig {
  if (!raw?.trim()) return DEFAULT_THEME
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!isThemeObject(parsed)) return DEFAULT_THEME

    const catalogKey = typeof parsed.catalogKey === 'string' ? parsed.catalogKey : undefined
    const customPalette = isPaletteColors(parsed.customPalette) ? parsed.customPalette : undefined

    if (catalogKey) {
      const keyParts = parseCatalogKey(catalogKey)
      if (keyParts?.styleId === CUSTOM_STYLE_ID && customPalette) {
        const customStyle = {
          id: CUSTOM_STYLE_ID,
          nome: 'Personalizado',
          vibe: 'Cores escolhidas por si.',
          ...customPalette,
        }
        return { ...buildThemeFromPalette(customStyle, catalogKey), customPalette }
      }
      const entry = findCatalogStyleByKey(catalogKey)
      if (entry) return buildThemeFromPalette(entry.style, catalogKey)
    }

    const stored: AppThemeConfig = {
      bg: parsed.bg || DEFAULT_THEME.bg,
      bgCard: parsed.bgCard || DEFAULT_THEME.bgCard,
      bgElevated: parsed.bgElevated || DEFAULT_THEME.bgElevated,
      text: parsed.text || DEFAULT_THEME.text,
      textMuted: parsed.textMuted || DEFAULT_THEME.textMuted,
      accent: parsed.accent || DEFAULT_THEME.accent,
      accentHover: parsed.accentHover || DEFAULT_THEME.accentHover,
      accentForeground: parsed.accentForeground || DEFAULT_THEME.accentForeground,
      border: parsed.border || DEFAULT_THEME.border,
      borderSubtle: parsed.borderSubtle || DEFAULT_THEME.borderSubtle,
      radius: parsed.radius || DEFAULT_THEME.radius,
      radiusSm: parsed.radiusSm || DEFAULT_THEME.radiusSm,
      catalogKey,
      customPalette,
    }

    return refineStoredTheme(stored)
  } catch {
    return DEFAULT_THEME
  }
}

export function serializeTheme(theme: AppThemeConfig): string {
  return JSON.stringify(theme)
}

/** Landing comercial e páginas legais públicas — sem tema white-label do tenant. */
export function isMarketingLandingPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/'
  if (path === '/privacidade' || path === '/termos' || path === '/eliminar-conta') return true
  if (path !== '/') return false
  if (hasTenantBound()) return false
  return true
}

/** Restaura o documento para a landing comercial (sem variáveis inline do tenant). */
export function applyMarketingLandingDocumentTheme(): void {
  clearAppTheme()
  const root = document.documentElement
  root.style.backgroundColor = '#ffffff'
  if (document.body) {
    document.body.style.backgroundColor = '#ffffff'
    document.body.style.removeProperty('color')
  }
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', MARKETING_LANDING_THEME_COLOR)
}

export function applyTheme(theme: AppThemeConfig): void {
  const root = document.documentElement
  for (const [key, cssVar] of Object.entries(THEME_MAP) as Array<[ThemeCssKey, string]>) {
    root.style.setProperty(cssVar, theme[key])
  }
  root.style.backgroundColor = theme.bg
  if (document.body) document.body.style.backgroundColor = theme.bg
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', theme.bg)
}

function themeCacheSlug(): string {
  return (getTenantSlug() || getAdminTenantSlug() || '').trim().toLowerCase()
}

function themeCacheKey(): string | null {
  const slug = themeCacheSlug()
  return slug ? `${THEME_CACHE_PREFIX}${slug}` : null
}

/** Persiste tema resolvido para evitar flash ao refrescar a página. */
export function saveThemeToCache(theme: AppThemeConfig): void {
  const key = themeCacheKey()
  if (!key) return
  try {
    localStorage.setItem(key, serializeTheme(theme))
  } catch {
    /* ignore */
  }
}

export function readCachedTheme(): AppThemeConfig | null {
  const key = themeCacheKey()
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return parseTheme(raw)
  } catch {
    return null
  }
}

export function applyCachedTheme(): boolean {
  const cached = readCachedTheme()
  if (!cached) return false
  applyTheme(cached)
  return true
}

/** Chamada síncrona antes do React renderizar (complementa o script em index.html). */
export function bootstrapAppTheme(): void {
  if (typeof window === 'undefined') return
  const path = window.location.pathname
  if (isPlatformPath(path)) return
  if (isMarketingLandingPath(path)) {
    applyMarketingLandingDocumentTheme()
    return
  }
  applyCachedTheme()
}

/** Variáveis CSS para pré-visualização isolada (não afecta o documento). */
export function themeToScopeStyle(theme: AppThemeConfig): CSSProperties {
  return {
    background: theme.bg,
    color: theme.text,
    borderColor: theme.border,
    ['--bg' as string]: theme.bg,
    ['--bg-card' as string]: theme.bgCard,
    ['--bg-elevated' as string]: theme.bgElevated,
    ['--text' as string]: theme.text,
    ['--text-muted' as string]: theme.textMuted,
    ['--accent' as string]: theme.accent,
    ['--accent-hover' as string]: theme.accentHover,
    ['--accent-foreground' as string]: theme.accentForeground,
    ['--border' as string]: theme.border,
    ['--border-subtle' as string]: theme.borderSubtle,
    ['--preview-accent' as string]: theme.accent,
    ['--preview-accent-hover' as string]: theme.accentHover,
  }
}

/** Remove variáveis inline do tema do salão (ex.: ao entrar no super-admin ou na landing). */
export function clearAppTheme(): void {
  const root = document.documentElement
  for (const cssVar of Object.values(THEME_MAP)) {
    root.style.removeProperty(cssVar)
  }
  root.style.removeProperty('background-color')
  if (document.body) {
    document.body.style.removeProperty('background-color')
    document.body.style.removeProperty('color')
  }
}

export async function loadAndApplyTheme(): Promise<void> {
  try {
    const res = await fetchApi('/api/conteudo/tema_app')
    const data = await handleJson<{ valor: string | null }>(res)
    const theme = parseTheme(data?.valor ?? null)
    applyTheme(theme)
    saveThemeToCache(theme)
  } catch {
    if (!applyCachedTheme()) applyTheme(DEFAULT_THEME)
  }
}
